import React, { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  FaPlus, FaSearch, FaFileInvoiceDollar, FaExclamationTriangle, FaAddressBook,
  FaPrint, FaCopy, FaSpinner,
} from 'react-icons/fa'
import { Card, Spinner, Banner, PageHeader, EmptyState, Toast, useToast, shade } from '../components/ui'
import { InvoicingPreviewNotice } from '../components/Notices'
import { money, number, formatDate } from '../lib/format'
import { KINDS, KIND_ORDER, STATUSES } from '../lib/invoice'
import { STATUS, SERIES, CHROME } from '../lib/viz'
import * as api from '../lib/api'

/**
 * Every document the shop has issued.
 *
 * Sorted newest first and filtered client-side: a shop writes a few hundred of
 * these a year, so fetching the lot once and filtering in memory is faster and
 * far more responsive than a round trip per keystroke.
 *
 * The four tiles answer the questions actually worth asking about a stack of
 * invoices — what is owed, what is late, what has been settled, and what is
 * still sitting unfinished — rather than counting rows.
 */

// Status colour is a secondary cue only: every chip carries its word, and
// overdue additionally carries an icon, so nothing depends on hue alone.
const TONE = {
  draft: CHROME.inkMuted,
  sent: SERIES,
  paid: STATUS.good,
  cancelled: STATUS.serious,
  overdue: STATUS.critical,
}

const StatusChip = ({ invoice }) => {
  const key = invoice.is_overdue ? 'overdue' : invoice.status
  const color = TONE[key] ?? CHROME.inkMuted
  const label = invoice.is_overdue ? 'Overdue' : STATUSES[invoice.status]?.label ?? invoice.status
  return (
    <span className="badge" style={{ backgroundColor: `${color}1f`, color: shade(color, 0.45) }}>
      {invoice.is_overdue && <FaExclamationTriangle className="text-[8px]" aria-hidden="true" />}
      {label}
    </span>
  )
}

const InvoicesPage = () => {
  const navigate = useNavigate()
  const [invoices, setInvoices] = useState([])
  const [state, setState] = useState({ loading: true, error: null })
  const [search, setSearch] = useState('')
  const [kind, setKind] = useState('all')
  const [status, setStatus] = useState('all')
  const [busy, setBusy] = useState(null)
  const { toast, notify, notifyError, dismiss } = useToast()

  const load = async () => {
    setState({ loading: true, error: null })
    try {
      setInvoices(await api.getInvoices())
      setState({ loading: false, error: null })
    } catch (err) {
      setState({ loading: false, error: err })
    }
  }

  useEffect(() => {
    load()
  }, [])

  const visible = useMemo(() => {
    const query = search.trim().toLowerCase()
    return invoices.filter((invoice) => {
      if (kind !== 'all' && invoice.kind !== kind) return false
      if (status === 'overdue' ? !invoice.is_overdue : status !== 'all' && invoice.status !== status) {
        return false
      }
      if (!query) return true
      return (
        invoice.number.toLowerCase().includes(query) ||
        (invoice.bill_to_name ?? '').toLowerCase().includes(query) ||
        (invoice.reference ?? '').toLowerCase().includes(query)
      )
    })
  }, [invoices, search, kind, status])

  const tiles = useMemo(() => {
    // Only documents that actually represent money owed count toward what is
    // outstanding. A quote is an offer, and counting one as revenue flatters the
    // figure by exactly the amount nobody has agreed to pay.
    const owed = invoices.filter((i) => KINDS[i.kind]?.owed && i.status === 'sent')
    const overdue = owed.filter((i) => i.is_overdue)
    const month = new Date().toISOString().slice(0, 7)
    const paidThisMonth = invoices.filter(
      (i) => i.status === 'paid' && (i.paid_at ?? i.issue_date ?? '').slice(0, 7) === month,
    )
    return {
      outstanding: owed.reduce((sum, i) => sum + Number(i.balance_due ?? i.total), 0),
      outstandingCount: owed.length,
      overdue: overdue.reduce((sum, i) => sum + Number(i.balance_due ?? i.total), 0),
      overdueCount: overdue.length,
      paid: paidThisMonth.reduce((sum, i) => sum + Number(i.total), 0),
      paidCount: paidThisMonth.length,
      drafts: invoices.filter((i) => i.status === 'draft').length,
    }
  }, [invoices])

  const duplicate = async (invoice) => {
    setBusy(invoice.id)
    try {
      const copy = await api.duplicateInvoice(invoice.id)
      notify(`Copied to ${copy.number}.`)
      navigate(`/invoices/${copy.id}`)
    } catch (err) {
      notifyError(err)
    } finally {
      setBusy(null)
    }
  }

  if (state.loading) return <Spinner label="Loading documents…" />
  if (state.error) {
    return <Banner tone="error"><strong>Could not load invoices.</strong> {state.error.message}</Banner>
  }

  return (
    <div>
      <PageHeader title="Invoices" subtitle={`${number(invoices.length)} documents`}>
        <Link to="/suppliers" className="btn-ghost">
          <FaAddressBook className="text-[11px]" />
          Suppliers
        </Link>
        <Link to="/invoices/new?kind=quote" className="btn-ghost">
          <FaPlus className="text-[11px]" />
          New quote
        </Link>
        <Link to="/invoices/new" className="btn-primary">
          <FaPlus className="text-[11px]" />
          New invoice
        </Link>
      </PageHeader>

      <InvoicingPreviewNotice />

      <div className="mb-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Tile
          label="Outstanding"
          value={money(tiles.outstanding)}
          note={`${tiles.outstandingCount} sent, awaiting payment`}
        />
        <Tile
          label="Overdue"
          value={money(tiles.overdue)}
          note={tiles.overdueCount > 0 ? `${tiles.overdueCount} past its due date` : 'Nothing late'}
          tone={tiles.overdueCount > 0 ? STATUS.critical : undefined}
        />
        <Tile label="Paid this month" value={money(tiles.paid)} note={`${tiles.paidCount} settled`} />
        <Tile
          label="Drafts"
          value={number(tiles.drafts)}
          note={tiles.drafts > 0 ? 'Not sent to anyone yet' : 'Nothing unfinished'}
        />
      </div>

      <Card bodyClassName="">
        <div className="flex flex-wrap items-center gap-2 border-b border-hairline p-3">
          <div className="relative min-w-[200px] flex-1">
            <FaSearch
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[11px] text-ink-muted"
              aria-hidden="true"
            />
            <input
              className="input pl-8"
              placeholder="Search number, name or reference"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search documents"
            />
          </div>

          <select
            className="input input-sm w-auto"
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            aria-label="Filter by document type"
          >
            <option value="all">All types</option>
            {KIND_ORDER.map((k) => (
              <option key={k} value={k}>{KINDS[k].label}</option>
            ))}
          </select>

          <select
            className="input input-sm w-auto"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            aria-label="Filter by status"
          >
            <option value="all">Any status</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="overdue">Overdue</option>
            <option value="paid">Paid</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        {visible.length === 0 ? (
          <EmptyState
            icon={FaFileInvoiceDollar}
            title={invoices.length === 0 ? 'No documents yet' : 'Nothing matches'}
            action={
              invoices.length === 0 ? (
                <Link to="/invoices/new" className="btn-primary">
                  <FaPlus className="text-[11px]" />
                  Make the first one
                </Link>
              ) : (
                <button
                  onClick={() => { setSearch(''); setKind('all'); setStatus('all') }}
                  className="btn-ghost"
                >
                  Clear filters
                </button>
              )
            }
          >
            {invoices.length === 0
              ? 'Invoices, quotes, pro formas and credit notes all live here. Pick a supplier or customer and the details fill themselves in.'
              : 'Try a different search, or clear the filters.'}
          </EmptyState>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Number</th>
                  <th>Made out to</th>
                  <th>Type</th>
                  <th>Date</th>
                  <th>Due</th>
                  <th className="num">Total</th>
                  <th className="num">Outstanding</th>
                  <th>Status</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {visible.map((invoice) => (
                  <tr key={invoice.id}>
                    <td>
                      <Link
                        to={`/invoices/${invoice.id}`}
                        className="font-medium tabular-nums text-ink hover:text-brand-red"
                      >
                        {invoice.number}
                      </Link>
                      {invoice.reference && (
                        <div className="max-w-[140px] truncate text-2xs text-ink-muted" title={invoice.reference}>
                          {invoice.reference}
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="max-w-[220px] truncate text-ink">{invoice.bill_to_name}</div>
                      {invoice.supplier_name && invoice.supplier_name !== invoice.bill_to_name && (
                        <div className="text-2xs text-ink-muted">{invoice.supplier_name}</div>
                      )}
                    </td>
                    <td className="text-2xs text-ink-secondary">{KINDS[invoice.kind]?.label ?? invoice.kind}</td>
                    <td className="text-2xs text-ink-secondary">{formatDate(invoice.issue_date)}</td>
                    <td className="text-2xs text-ink-secondary">
                      {invoice.due_date ? formatDate(invoice.due_date) : '—'}
                      {invoice.is_overdue && invoice.days_to_due != null && (
                        <div className="text-2xs font-semibold" style={{ color: STATUS.critical }}>
                          {Math.abs(invoice.days_to_due)} days late
                        </div>
                      )}
                    </td>
                    <td className="num font-medium">{money(invoice.total)}</td>
                    <td className="num text-ink-secondary">
                      {Number(invoice.balance_due) > 0.005 && invoice.status !== 'cancelled'
                        ? money(invoice.balance_due)
                        : '—'}
                    </td>
                    <td><StatusChip invoice={invoice} /></td>
                    <td className="whitespace-nowrap text-right">
                      <a
                        href={`/invoices/${invoice.id}/print`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-subtle btn-sm px-1.5"
                        title="Print or save as PDF"
                      >
                        <FaPrint className="text-[10px]" />
                      </a>
                      <button
                        onClick={() => duplicate(invoice)}
                        disabled={busy === invoice.id}
                        className="btn-subtle btn-sm px-1.5"
                        title="Copy to a new draft"
                      >
                        {busy === invoice.id ? (
                          <FaSpinner className="animate-spin text-[10px]" />
                        ) : (
                          <FaCopy className="text-[10px]" />
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Toast toast={toast} onDismiss={dismiss} />
    </div>
  )
}

const Tile = ({ label, value, note, tone }) => (
  <div className="card p-3.5">
    <div className="text-2xs font-medium uppercase tracking-[0.08em] text-ink-muted">{label}</div>
    <div className="mt-1 text-xl font-semibold tabular-nums" style={tone ? { color: tone } : undefined}>
      {value}
    </div>
    {note && <div className="mt-0.5 text-2xs text-ink-muted">{note}</div>}
  </div>
)

export default InvoicesPage
